import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';

import { GosFormData } from '../../@types/pcse';
import { pcseService } from '../../api/pcse.service';
import { normalisePcseError } from '../../hooks/usePCSEClaims';
import Iconify from '../iconify';
import GosClaimPrintout from './GosClaimPrintout';

interface GosClaimPrintDialogProps {
  open: boolean;
  onClose: () => void;
  claimId?: string;
}

/**
 * Preview and print the official NHS GOS paper form for a claim.
 *
 * Printing happens in a detached window rather than via `@media print` on the
 * app: the dashboard shell (nav rail, sticky headers, MUI portals) fights any
 * attempt to print a subtree in place, and a claim form that prints with a
 * navigation sidebar across it is not a form anyone can file. "Save as PDF" in
 * the browser's own print dialog produces the PDF.
 */
export default function GosClaimPrintDialog({
  open,
  onClose,
  claimId,
}: GosClaimPrintDialogProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState<GosFormData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !claimId) return;
    let cancelled = false;

    setLoading(true);
    setError(null);
    pcseService
      .getClaimForm(claimId)
      .then((res) => {
        if (!cancelled) setForm(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(normalisePcseError(err).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, claimId]);

  const handlePrint = useCallback(() => {
    const node = printRef.current;
    if (!node) return;

    const printWindow = window.open('', '_blank', 'width=900,height=1200');
    if (!printWindow) {
      setError(
        'Your browser blocked the print window. Allow pop-ups for this site and try again.'
      );
      return;
    }

    // The component renders its own <style> block, so copying innerHTML carries
    // the full stylesheet across to the detached document.
    printWindow.document.write(
      `<!doctype html><html><head><title>NHS ${
        form?.claim.type ?? 'GOS'
      } claim ${form?.claim.id ?? ''}</title></head><body>${node.innerHTML}</body></html>`
    );
    printWindow.document.close();
    printWindow.focus();

    // Give the inlined signature images a tick to decode; printing before they
    // paint yields a form with empty signature boxes.
    const run = () => {
      printWindow.print();
      printWindow.close();
    };
    if (printWindow.document.readyState === 'complete') {
      setTimeout(run, 250);
    } else {
      printWindow.onload = () => setTimeout(run, 250);
    }
  }, [form]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>NHS GOS claim form</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Stack alignItems="center" py={6}>
            <CircularProgress />
          </Stack>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {form && (
          <Box
            ref={printRef}
            sx={{
              bgcolor: '#fff',
              // The preview mimics an A4 sheet so what is on screen is what
              // comes out of the printer.
              boxShadow: (theme) => theme.shadows[2],
              mx: 'auto',
            }}
          >
            <GosClaimPrintout form={form} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="contained"
          disabled={!form}
          onClick={handlePrint}
          startIcon={<Iconify icon="eva:printer-outline" />}
        >
          Print / Save as PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}
